<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div class="md:flex md:items-center md:justify-between">
      <div class="flex-1">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">Edit Company</h1>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Update company information and settings
        </p>
      </div>
      <div class="mt-4 flex space-x-3 md:mt-0 md:ml-4">
        <Link
          :href="route('site-owner.companies.show', company.id)"
          class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <ArrowLeftIcon class="h-5 w-5 mr-2" />
          Cancel
        </Link>
      </div>
    </div>

    <!-- Edit Form -->
    <form @submit.prevent="submitForm">
      <div class="space-y-6">
        <!-- Basic Information -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Basic Information
            </h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <!-- Company Name -->
            <div>
              <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company Name <span class="text-red-500">*</span>
              </label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.name }"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.name }}
              </p>
            </div>

            <!-- Tenant Database (Read-only) -->
            <div>
              <label for="tenant_db_name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Tenant Database
              </label>
              <input
                id="tenant_db_name"
                v-model="company.tenant_db_name"
                type="text"
                disabled
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-mono sm:text-sm"
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Cannot be changed
              </p>
            </div>

            <!-- Website -->
            <div>
              <label for="website" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Website
              </label>
              <input
                id="website"
                v-model="form.website"
                type="url"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.website }"
              />
              <p v-if="errors.website" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.website }}
              </p>
            </div>

            <!-- Phone -->
            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone
              </label>
              <input
                id="phone"
                v-model="form.phone"
                type="tel"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.phone }"
              />
              <p v-if="errors.phone" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.phone }}
              </p>
            </div>

            <!-- Industry Type -->
            <div>
              <label for="industry_type" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Industry Type
              </label>
              <input
                id="industry_type"
                v-model="form.industry_type"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>

            <!-- Company Size -->
            <div>
              <label for="company_size" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company Size
              </label>
              <input
                id="company_size"
                v-model="form.company_size"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>
          </div>
        </div>

        <!-- Address Information -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              Address
            </h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <!-- Address Line 1 -->
            <div>
              <label for="address_line_1" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Address Line 1 <span class="text-red-500">*</span>
              </label>
              <input
                id="address_line_1"
                v-model="form.address_line_1"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.address_line_1 }"
              />
              <p v-if="errors.address_line_1" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.address_line_1 }}
              </p>
            </div>

            <!-- Address Line 2 -->
            <div>
              <label for="address_line_2" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Address Line 2
              </label>
              <input
                id="address_line_2"
                v-model="form.address_line_2"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              />
            </div>

            <!-- City -->
            <div>
              <label for="city" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                City <span class="text-red-500">*</span>
              </label>
              <input
                id="city"
                v-model="form.city"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.city }"
              />
              <p v-if="errors.city" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.city }}
              </p>
            </div>

            <!-- State / Province -->
            <div>
              <label for="state_province" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                State / Province <span class="text-red-500">*</span>
              </label>
              <input
                id="state_province"
                v-model="form.state_province"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.state_province }"
              />
              <p v-if="errors.state_province" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.state_province }}
              </p>
            </div>

            <!-- Postal Code -->
            <div>
              <label for="postal_code" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Postal Code <span class="text-red-500">*</span>
              </label>
              <input
                id="postal_code"
                v-model="form.postal_code"
                type="text"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.postal_code }"
              />
              <p v-if="errors.postal_code" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.postal_code }}
              </p>
            </div>

            <!-- Country -->
            <div>
              <label for="country_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Country <span class="text-red-500">*</span>
              </label>
              <select
                id="country_id"
                v-model="form.country_id"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.country_id }"
              >
                <option value="">Select a country</option>
                <option v-for="country in countries" :key="country.id" :value="country.id">
                  {{ country.name }}
                </option>
              </select>
              <p v-if="errors.country_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.country_id }}
              </p>
            </div>
          </div>
        </div>

        <!-- System Settings -->
        <div class="bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div class="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
              System Settings
            </h3>
          </div>
          <div class="px-6 py-4 space-y-4">
            <!-- Currency -->
            <div>
              <label for="currency_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Currency <span class="text-red-500">*</span>
              </label>
              <select
                id="currency_id"
                v-model="form.currency_id"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.currency_id }"
              >
                <option value="">Select a currency</option>
                <option v-for="currency in currencies" :key="currency.id" :value="currency.id">
                  {{ currency.code }} - {{ currency.name }}
                </option>
              </select>
              <p v-if="errors.currency_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.currency_id }}
              </p>
            </div>

            <!-- Timezone -->
            <div>
              <label for="timezone_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Timezone <span class="text-red-500">*</span>
              </label>
              <select
                id="timezone_id"
                v-model="form.timezone_id"
                required
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.timezone_id }"
              >
                <option value="">Select a timezone</option>
                <option v-for="timezone in timezones" :key="timezone.id" :value="timezone.id">
                  {{ timezone.display_name || timezone.name }}
                </option>
              </select>
              <p v-if="errors.timezone_id" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.timezone_id }}
              </p>
            </div>

            <!-- Storage Quota -->
            <div>
              <label for="storage_quota_gb" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Storage Quota (GB)
              </label>
              <input
                id="storage_quota_gb"
                v-model.number="form.storage_quota_gb"
                type="number"
                min="0"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                :class="{ 'border-red-500': errors.storage_quota_gb }"
              />
              <p v-if="errors.storage_quota_gb" class="mt-1 text-sm text-red-600 dark:text-red-400">
                {{ errors.storage_quota_gb }}
              </p>
            </div>

            <!-- Company Status -->
            <div>
              <label for="company_status" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Company Status
              </label>
              <select
                id="company_status"
                v-model="form.company_status"
                class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-white sm:text-sm"
              >
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <!-- Is Active -->
            <div class="flex items-center">
              <input
                id="is_active"
                v-model="form.is_active"
                type="checkbox"
                class="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
              />
              <label for="is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                Company is active
              </label>
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div class="flex justify-end space-x-3">
          <Link
            :href="route('site-owner.companies.show', company.id)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </Link>
          <button
            type="submit"
            :disabled="processing"
            class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span v-if="processing">Saving...</span>
            <span v-else>Save Changes</span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  company: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    required: true,
  },
  currencies: {
    type: Array,
    required: true,
  },
  timezones: {
    type: Array,
    required: true,
  },
});

const page = usePage();
const processing = ref(false);
const errors = ref({});

// Form data
const form = reactive({
  name: props.company.name,
  address_line_1: props.company.address_line_1,
  address_line_2: props.company.address_line_2 || '',
  city: props.company.city,
  state_province: props.company.state_province,
  postal_code: props.company.postal_code,
  country_id: props.company.country_id,
  currency_id: props.company.currency_id,
  timezone_id: props.company.timezone_id,
  phone: props.company.phone || '',
  website: props.company.website || '',
  industry_type: props.company.industry_type || '',
  company_size: props.company.company_size || '',
  storage_quota_gb: props.company.storage_quota_gb || 0,
  is_active: props.company.is_active,
  company_status: props.company.company_status,
});

// Submit form
const submitForm = () => {
  processing.value = true;
  errors.value = {};

  router.put(route('site-owner.companies.update', props.company.id), form, {
    preserveScroll: true,
    onSuccess: () => {
      processing.value = false;
    },
    onError: (err) => {
      errors.value = err;
      processing.value = false;
    },
  });
};
</script>
